import type { ProjectConfig, Scene, Sequence } from "../models/production";
import type { Shot } from "../types";
import { deriveHierarchyFromShotCode } from "../shared/shotCodeParser";
import {
  ScriptSceneInput,
  buildSceneId,
  inferSequenceNumberFromScene,
  mapScriptSceneToSceneRecord,
  normalizeSceneNumber,
} from "../shared/sceneMappers";

export interface SceneUpsertResult {
  scenes: Scene[];
  upserts: Scene[];
}

export interface SceneShotLinkResult {
  scenePatches: Array<{ sceneId: string; patch: Partial<Scene> }>;
  shotPatches: Array<{ shotId: string; patch: Partial<Shot> }>;
  sequencePatches: Array<{ sequenceId: string; patch: Partial<Sequence> }>;
}

function buildSequenceId(projectId: string, seasonNumber: number | undefined, episodeCode: string | undefined, sequenceNumber: number) {
  if (episodeCode) return `${projectId}:sequence:${episodeCode}:${String(sequenceNumber).padStart(3, "0")}`;
  if (seasonNumber !== undefined) return `${projectId}:sequence:s${seasonNumber}:${String(sequenceNumber).padStart(3, "0")}`;
  return `${projectId}:sequence:${String(sequenceNumber).padStart(3, "0")}`;
}

export function inferSequenceFromScene(
  sceneData: ScriptSceneInput,
  projectConfig?: Partial<ProjectConfig> | null
) {
  const sceneNumber = normalizeSceneNumber(sceneData.sceneNumber);
  return inferSequenceNumberFromScene(sceneNumber, projectConfig, typeof sceneData.sequenceNumber === "number" ? sceneData.sequenceNumber : undefined);
}

export function upsertScriptSceneData(args: {
  projectId: string;
  seasonId?: string;
  episodeId: string;
  sceneData: ScriptSceneInput[];
  existingScenes?: Scene[];
  projectConfig?: Partial<ProjectConfig> | null;
}): SceneUpsertResult {
  const existingBySceneNumber = new Map((args.existingScenes || []).map((scene) => [normalizeSceneNumber(scene.sceneNumber), scene]));
  const upserts = args.sceneData.map((input) =>
    mapScriptSceneToSceneRecord({
      projectId: args.projectId,
      seasonId: args.seasonId,
      episodeId: args.episodeId,
      existingScene: existingBySceneNumber.get(normalizeSceneNumber(input.sceneNumber)),
      input,
      projectConfig: args.projectConfig,
    })
  );

  const merged = new Map((args.existingScenes || []).map((scene) => [scene.id, scene]));
  for (const scene of upserts) {
    merged.set(scene.id, scene);
  }

  return {
    scenes: Array.from(merged.values()),
    upserts,
  };
}

export function linkScenesToSequencesAndShots(args: {
  projectId: string;
  scenes: Scene[];
  shots: Shot[];
  sequences?: Sequence[];
  projectConfig?: Partial<ProjectConfig> | null;
}): SceneShotLinkResult {
  const scenePatches: Array<{ sceneId: string; patch: Partial<Scene> }> = [];
  const shotPatches: Array<{ shotId: string; patch: Partial<Shot> }> = [];
  const sequencePatches: Array<{ sequenceId: string; patch: Partial<Sequence> }> = [];
  const sceneUpdates = new Map<string, Partial<Scene>>();
  const sequenceSceneIds = new Map<string, Set<string>>();

  for (const scene of args.scenes) {
    const sequenceNumber = scene.sequenceNumber ?? inferSequenceFromScene({ sceneNumber: scene.sceneNumber }, args.projectConfig);
    const relatedShots = args.shots.filter((shot) => {
      const hierarchy = deriveHierarchyFromShotCode(shot.shotCode, args.projectConfig?.shotCodeParserConfig);
      const episodeMatches = !scene.episodeId || !shot.episodeId || shot.episodeId === scene.episodeId;
      const sequenceMatches = sequenceNumber !== undefined && hierarchy.sequenceNumber === sequenceNumber;
      return episodeMatches && sequenceMatches;
    });

    const patch: Partial<Scene> = {
      relatedShotIds: relatedShots.map((shot) => shot.id),
    };

    if (sequenceNumber !== undefined) {
      const episodeCode = scene.episodeId.split(":").at(-1);
      const sequenceId = scene.sequenceId || buildSequenceId(args.projectId, undefined, episodeCode, sequenceNumber);
      patch.sequenceId = sequenceId;
      sequenceSceneIds.set(sequenceId, new Set([...(sequenceSceneIds.get(sequenceId) || new Set()), scene.id]));
    }

    sceneUpdates.set(scene.id, patch);

    for (const shot of relatedShots) {
      shotPatches.push({
        shotId: shot.id,
        patch: {
          sceneId: shot.sceneId || scene.id,
          sequenceId: shot.sequenceId || patch.sequenceId,
          episodeId: shot.episodeId || scene.episodeId,
          seasonId: shot.seasonId || scene.seasonId,
        },
      });
    }
  }

  for (const [sceneId, patch] of sceneUpdates) {
    scenePatches.push({ sceneId, patch });
  }

  for (const [sequenceId, sceneIds] of sequenceSceneIds) {
    sequencePatches.push({
      sequenceId,
      patch: {
        sceneIds: Array.from(sceneIds),
      },
    });
  }

  return {
    scenePatches,
    shotPatches,
    sequencePatches,
  };
}

export function buildSceneRecordsFromScriptDocument(args: {
  projectId: string;
  episodeId: string;
  seasonId?: string;
  parsedScenes: ScriptSceneInput[];
  existingScenes?: Scene[];
  projectConfig?: Partial<ProjectConfig> | null;
}) {
  return upsertScriptSceneData({
    projectId: args.projectId,
    seasonId: args.seasonId,
    episodeId: args.episodeId,
    sceneData: args.parsedScenes,
    existingScenes: args.existingScenes,
    projectConfig: args.projectConfig,
  });
}

export function ensureSceneRecordForShot(args: {
  projectId: string;
  episodeId: string;
  shot: Shot;
  projectConfig?: Partial<ProjectConfig> | null;
}) {
  const hierarchy = deriveHierarchyFromShotCode(args.shot.shotCode, args.projectConfig?.shotCodeParserConfig);
  if (hierarchy.sequenceNumber === undefined) return null;

  const sceneNumber = String(hierarchy.sequenceNumber);
  return {
    id: buildSceneId(args.episodeId, sceneNumber),
    projectId: args.projectId,
    episodeId: args.episodeId,
    seasonId: args.shot.seasonId,
    sequenceId: args.shot.sequenceId,
    sceneNumber,
    sequenceNumber: hierarchy.sequenceNumber,
    relatedShotIds: [args.shot.id],
    relatedAssetIds: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  } satisfies Scene;
}
